/**
 * Ledger Export Page
 *
 * Accountant-ready ledger export functionality:
 * - Date range selection
 * - Export format (CSV, QuickBooks)
 * - Download functionality
 * - Export history
 *
 * Epic: MVP 1.21.3 - Guide Dashboard
 * Story: US-1.21.3-18: CSV Ledger Export (8 SP)
 *
 * CSV Format: 15 columns (Date, Type, Category, Description, Reference, Customer,
 * Gross, Fees, Net, Payment Method, Stripe Payout ID, Experience, Trip Date, Guests, Notes)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiRequest from '../../services/api';
import {
  Download,
  Calendar as _Calendar,
  FileText,
  AlertCircle as _AlertCircle,
  CheckCircle,
  Clock,
  DollarSign as _DollarSign,
  AlertCircle
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';

export default function LedgerExportPage() {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [format, setFormat] = useState('csv'); // csv, quickbooks
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [exportHistory, setExportHistory] = useState([]);

  // Fetch export history on mount
  useEffect(() => {
    fetchExportHistory();
  }, []);

  const fetchExportHistory = async () => {
    try {
      const response = await apiRequest('/api/v1/guides/earnings/export/history', { method: 'GET' });
      if (response.success) {
        setExportHistory(response.data.exports);
      }
    } catch (err) {
      console.error('Error fetching export history:', err);
      if (err.status === 401) {
        // Session expired - api.js will handle redirect
      }
    }
  };

  // Handle export
  const handleExport = async (  _e) => {
    _e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Validate dates
      if (!startDate || !endDate) {
        setError('Please select both start and end dates');
        return;
      }

      const endpoint = format === 'quickbooks'
        ? '/api/v1/guides/earnings/export/quickbooks'
        : '/api/v1/guides/earnings/export/csv';

      const response = await apiRequest(endpoint, {
        method: 'POST',
        body: JSON.stringify({
          start_date: startDate,
          end_date: endDate,
          upload_to_s3: false
        })
      });

      if (response.success) {
        const { csv, file_name, record_count, summary } = response.data;

        // Create download
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = file_name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        setSuccess(`Successfully exported ${record_count} transactions (${formatCurrency(summary.totalNet)} net)`);

        // Refresh export history
        await fetchExportHistory();
      }
    } catch (err) {
      console.error('Error exporting ledger:', err);
      if (err.status === 401) {
        setError('Session expired. Please log in again.');
      } else {
        setError(err.message || 'Failed to export ledger. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Handle navigation
  const handleNavigate = (id, path) => {
    if (path) {
      navigate(path);
    }
  };

  return (
    <DashboardLayout
      userRole="guide"
      activeNavId="earnings"
      onNavigate={handleNavigate}
    >
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Ledger Export</h1>
            <p className="mt-1 text-sm text-gray-500">
              Download accountant-ready transaction reports
            </p>
          </div>
          <button
            onClick={() => handleNavigate('earnings', '/guide/earnings')}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Back to Earnings
          </button>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="flex items-center space-x-2 p-4 bg-green-50 border border-green-200 rounded-lg">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <p className="text-sm text-green-800">{success}</p>
          </div>
        )}

        {error && (
          <div className="flex items-center space-x-2 p-4 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Export Form */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Generate Export</h2>

          <form onSubmit={handleExport} className="space-y-4">
            {/* Date Range */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  id="startDate"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  id="endDate"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
            </div>

            {/* Format Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Export Format
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${format === 'csv' ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <input
                    type="radio"
                    value="csv"
                    checked={format === 'csv'}
                    onChange={(e) => setFormat(e.target.value)}
                    className="mr-3"
                  />
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">Standard CSV</p>
                    <p className="text-xs text-gray-600 mt-1">15-column format for spreadsheets and accounting software</p>
                  </div>
                </label>

                <label className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${format === 'quickbooks' ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <input
                    type="radio"
                    value="quickbooks"
                    checked={format === 'quickbooks'}
                    onChange={(e) => setFormat(e.target.value)}
                    className="mr-3"
                  />
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">QuickBooks</p>
                    <p className="text-xs text-gray-600 mt-1">QBO-compatible format for direct import</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Export Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <Download className="w-5 h-5" />
                  <span>Generate & Download</span>
                </>
              )}
            </button>
          </form>
        </div>

        {/* Export History */}
        {exportHistory.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">Export History</h2>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                {exportHistory.map((exp) => (
                  <div key={exp.export_id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex items-center space-x-4">
                      <FileText className="w-8 h-8 text-blue-600" />
                      <div>
                        <p className="text-sm font-semibold text-gray-900">
                          {exp.format === 'quickbooks' ? 'QuickBooks' : 'CSV'} Export
                        </p>
                        <p className="text-xs text-gray-600">
                          {formatDate(exp.start_date)} - {formatDate(exp.end_date)}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {exp.record_count} transactions
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500 flex items-center space-x-1">
                        <Clock className="w-3 h-3" />
                        <span>{formatDate(exp.created_at)}</span>
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">CSV Column Reference</h3>
          <div className="text-xs text-blue-800 space-y-1">
            <p><strong>15 Columns:</strong> Date, Type, Category, Description, Reference, Customer, Gross, Fees, Net, Payment Method, Stripe Payout ID, Experience, Trip Date, Guests, Notes</p>
            <p className="mt-2"><strong>Summary Row:</strong> Totals for gross revenue, platform fees (8%), and net earnings</p>
            <p className="mt-2"><strong>Validation:</strong> Net = Gross - Fees for all transactions</p>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
